import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { EstimatedMonthlyBudget } from '@bingads-webui-react/budget-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getEstimatedMonthlyBudgetStyles } from './styles/get-estimated-monthly-budget-styles';
import { BudgetInput } from './budget-input';
import { Slider } from './slider';
import { serializeCurrencies } from './serialize-currencies';
import { getMonthlyBudget } from './helpers';
import { BudgetShimmer } from './budget-shimmer';

export const BudgetComponent = ({
  showDropdown,
  currencies,
  currency,
  onCurrencyChange,
  budget,
  onBudgetChange,
  // TODO: Better make this props straightforward and split it
  budgetSuggestion,
  isLoading,
  logger,
  onNotifyValidation,
  onRenderInlineHelp,
  withSlider,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getEstimatedMonthlyBudgetStyles);

  const budgetSchema = useMemo(() => ({
    name: `Product Schema ${_.get(budgetSuggestion, ['Currency'])}`,
    type: 'object',
    properties: {
      budget: {
        type: 'number',
        required: true,
        maximum: _.get(budgetSuggestion, ['GlobalMaximumBudget', 'DailyBudget']),
        minimum: _.get(budgetSuggestion, ['GlobalMinimumBudget', 'DailyBudget']),
      },
    },
  }), [budgetSuggestion]);

  const serializedCurrencies = useMemo(() => serializeCurrencies(currencies, i18n), [currencies, i18n]);
  const [monthlyBudget, setMonthlyBudget] = useState();

  const onBudgetInputChange = useCallback((e) => {
    const value = _.get(e, ['target', 'value']);
    onBudgetChange(value);
    logger.trace(`[Budget] Budget amount updated: ${value}`);
  }, [logger, onBudgetChange]);

  const onBudgetSliderChange = useCallback((val) => {
    const shouldLocalize = true;
    onBudgetChange(val, shouldLocalize);
    // budget value change by slider should always be valid
    onNotifyValidation({ isValid: true });
  }, [onBudgetChange, onNotifyValidation]);

  const onBudgetInputValidate = useCallback(({ data, isValid, messages }) => {
    onNotifyValidation({ data, isValid, messages });
  }, [onNotifyValidation]);

  const onSelectCurrency = useCallback((val) => {
    onCurrencyChange(val);
    logger.trace(`[Budget] Currency updated: ${val}`);
  }, [logger, onCurrencyChange]);

  useEffect(() => {
    const monthlyBudgetValue = budget && getMonthlyBudget(i18n.parseDecimal(budget));
    const formattedMonthyBudget = monthlyBudgetValue && i18n.formatDecimal(monthlyBudgetValue, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (!_.isNaN(formattedMonthyBudget)) {
      setMonthlyBudget(formattedMonthyBudget);
    } else {
      setMonthlyBudget('-');
    }
  }, [budget, i18n]);

  return (
    isLoading ?
      <BudgetShimmer /> :
      <div className="budget-component-container">
        <BudgetInput
          budgetValue={budget}
          currencies={serializedCurrencies}
          showDropdown={showDropdown}
          isCurrencyDisabled={false}
          onBudgetInputChange={onBudgetInputChange}
          onBudgetValidate={onBudgetInputValidate}
          onSelectCurrency={onSelectCurrency}
          schema={budgetSchema}
          selectedCurrency={currency}
          onRenderInlineHelp={onRenderInlineHelp}
        />
        {withSlider &&
          <Slider
            budgetValue={i18n.parseDecimal(budget)}
            onBudgetSliderChange={onBudgetSliderChange}
            budgetSuggestion={budgetSuggestion}
            ariaLabel={i18n.getString(_TL_('Budget value'))}
          />
        }
        <EstimatedMonthlyBudget
          i18n={i18n}
          value={monthlyBudget}
          selectedCurrencyText={currency}
          classes={classes}
        />
      </div>
  );
};

BudgetComponent.propTypes = {
  showDropdown: PropTypes.bool,
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
  currencies: PropTypes.arrayOf(PropTypes.shape({})),
  currency: PropTypes.string.isRequired,
  onCurrencyChange: PropTypes.func,
  budget: PropTypes.number.isRequired,
  onBudgetChange: PropTypes.func.isRequired,
  onNotifyValidation: PropTypes.func.isRequired,
  budgetSuggestion: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  onRenderInlineHelp: PropTypes.func,
  withSlider: PropTypes.bool,
};

BudgetComponent.defaultProps = {
  currencies: [],
  onCurrencyChange: _.noop,
  showDropdown: false,
  budgetSuggestion: {},
  isLoading: true,
  logger: {
    trace: _.noop,
    error: _.noop,
  },
  onRenderInlineHelp: _.noop,
  withSlider: true,
};
